import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faUserShield, faInfoCircle, faSearch, faKey, faFilter } from "@fortawesome/free-solid-svg-icons";

const UserRolePermissionsPage_v2 = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [showOnlyRolePermissions, setShowOnlyRolePermissions] = useState(false); // Toggle filter state

  useEffect(() => {
    fetchPermissions();
    fetchRolePermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/role_permissions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setPermissions(response.data);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setIsLoading(false); // Stop loading after fetch
    }
  };

  const fetchRolePermissions = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/roles/${roleId}/permissions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setRolePermissions(response.data);
    } catch (error) {
      console.error("Error fetching role permissions:", error);
    }
  };

  const handlePermissionChange = async (permissionId, isChecked) => {
    try {
      const method = isChecked ? "post" : "delete";
      await axios[method](
        `${BASE_URL}/admin/roles/${roleId}/permissions/${permissionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );
      Swal.fire("Success!", "Permission updated successfully.", "success");
      fetchRolePermissions();
    } catch (error) {
      Swal.fire("Error!", "Failed to update permission.", "error");
    }
  };

  const filteredPermissions = permissions.filter((permission) => {
    const matchesSearch = permission.permission_name.toLowerCase().includes(searchTerm.toLowerCase());
    if (showOnlyRolePermissions) {
      return matchesSearch && rolePermissions.some((rp) => rp.permission_id === permission.permission_id);
    }
    return matchesSearch;
  });

  return (
    <div className="bg-gray-50 min-h-screen p-2">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-sm p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-500 hover:text-blue-700 mb-3 text-sm"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>
        <h1 className="text-xl font-bold mb-4 flex items-center">
          <FontAwesomeIcon icon={faKey} className="mr-2" />
          Permissions for Role ID: {roleId}
        </h1>

        {/* Search Bar and Toggle Filter */}
        <div className="mb-4 flex items-center">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search permissions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-2.5 top-2.5 text-gray-400 text-sm"
            />
          </div>
          <button
            onClick={() => setShowOnlyRolePermissions(!showOnlyRolePermissions)}
            className={`ml-2 p-2 rounded-md text-sm flex items-center ${
              showOnlyRolePermissions ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            }`}
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            {showOnlyRolePermissions ? "Show All Permissions" : "Show Only My Permissions"}
          </button>
        </div>

        {/* Scrollable Permissions Container */}
        <div className="overflow-y-auto max-h-[50vh] pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isLoading ? (
              // Loading Skeleton
              Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-gray-100 p-3 rounded-md animate-pulse"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-4 w-4 bg-gray-300 rounded"></div>
                      <div className="ml-2 h-4 bg-gray-300 rounded w-1/2"></div>
                    </div>
                    <div className="h-4 w-4 bg-gray-300 rounded"></div>
                  </div>
                  <div className="mt-2 h-3 bg-gray-300 rounded w-3/4"></div>
                </div>
              ))
            ) : (
              // Actual Permissions
              filteredPermissions.map((permission) => (
                <div
                  key={permission.permission_id}
                  className="bg-gray-100 p-3 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={rolePermissions.some((rp) => rp.permission_id === permission.permission_id)}
                        onChange={(e) => handlePermissionChange(permission.permission_id, e.target.checked)}
                        className="form-checkbox h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-base font-medium">{permission.permission_name}</span>
                    </div>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-gray-400 hover:text-gray-600 cursor-pointer text-sm"
                    />
                  </div>
                  {permission.description && (
                    <p className="text-xs text-gray-600 mt-1">{permission.description}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRolePermissionsPage_v2;